import { useLoadErrorToast } from '../shared/useLoadErrorToast';
import { useSearchInput } from '../shared/useSearchInput';
import { useExpandedPatients } from './useExpandedPatients';
import { useLeavingNotifications } from './useLeavingNotifications';
import { useNotificationFilters } from './useNotificationFilters';
import { useNotificationInboxQuery } from './useNotificationInboxQuery';
import { useNotificationListUrlState } from './useNotificationListUrlState';

const PAGE_SIZE = 20;

export function useNotificationsInbox() {
    const table = useNotificationListUrlState(PAGE_SIZE);
    const [searchInput, setSearchInput] = useSearchInput(table.search, table.setSearch);
    const { filters, more, moreActiveCount, apiFilters, assigneesResolved } =
        useNotificationFilters(table);
    const { query, loadMore } = useNotificationInboxQuery({
        filters: apiFilters,
        mode: table.mode,
        page: table.page,
        limit: table.limit,
        enabled: assigneesResolved
    });
    useLoadErrorToast(query.isError, query.error, 'Failed to load DM notifications');

    const { levels, practiceIds, assignees, tags } = table.filters;
    const hasFilters =
        levels.length > 0 ||
        practiceIds.length > 0 ||
        assignees.length > 0 ||
        tags.length > 0 ||
        table.search.length > 0;
    const clearFilters = () => {
        setSearchInput('');
        table.clearFilters();
    };
    const { folds, toggleExpanded } = useExpandedPatients(table.rows);
    const { leavingIds, hiddenIds, markLeaving } = useLeavingNotifications(query.rows);

    return {
        table,
        query,
        loadMore,
        search: { value: searchInput, onChange: setSearchInput },
        filters,
        more,
        moreActiveCount,
        hasFilters,
        clearFilters,
        folds,
        leavingIds,
        hiddenIds,
        markLeaving,
        toggleExpanded
    };
}

export type NotificationsInbox = ReturnType<typeof useNotificationsInbox>;
